import { useMemo } from 'react';
import { onlyDateFromServer, useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, IAccountDtoAccountTypeEnum, IEnrollmentDto, IEnrollmentDtoProofOfExpensesTypeEnum, IEnrollmentDtoStatusEnum  } from '@/dtos';
import { CoveragePeriod } from '@/modules/UseMyMoney/UseMyMoney.types';

export interface Enrollment {
  accountId?: string;
  policyId?: string;
  enrollmentTitle?: string;
  status?: IEnrollmentDtoStatusEnum;
  availableAmount?: number;
  submitClaimsDateEnd?: string;
  proofOfExpensesType?: IEnrollmentDtoProofOfExpensesTypeEnum;
  accountType?: IAccountDtoAccountTypeEnum;
  coveragePeriods?: CoveragePeriod[];
}

interface Props {
  employeeId?: string;
}

const useGetEnrollmentsQuery = ({ employeeId }: Props) => {
  const { data, isLoading } = useQuery([API_URL.ENROLLMENTS, employeeId], () =>
    api.get<IEnrollmentDto[]>(API_URL.ENROLLMENTS, {
      params: {
        employee_id: employeeId || undefined,
      },
    }),
    {
      enabled: Boolean(employeeId),
    }
  );

  const enrollments = useMemo(() => {
    if (!data?.data?.length) {
      return [];
    }

    const formattedEnrollments = data?.data?.map((enrollment): Enrollment => ({
      accountId: toString(enrollment?.account_id),
      policyId: toString(enrollment?.policy_id),
      enrollmentTitle: enrollment?.enrollment_title,
      status: enrollment?.status,
      availableAmount: enrollment?.available_amount,
      submitClaimsDateEnd: enrollment?.submit_claims_date_end
        ? onlyDateFromServer(`${enrollment?.submit_claims_date_end}`)
        : undefined,
      proofOfExpensesType: enrollment?.proof_of_expenses_type,
      accountType: enrollment?.account_type as IAccountDtoAccountTypeEnum,
      coveragePeriods: enrollment?.coverage_periods?.map((coveragePeriod) => ({
        coverageDateStart: coveragePeriod?.coverage_date_start
          ? onlyDateFromServer(`${coveragePeriod?.coverage_date_start}`)
          : undefined,
        coverageDateEnd: coveragePeriod?.coverage_date_end
          ? onlyDateFromServer(`${coveragePeriod?.coverage_date_end}`)
          : undefined,
      })) || [],
    }));

    return formattedEnrollments;
  }, [data?.data]);

  const activeEnrollments = useMemo(() => {
    return enrollments.filter(
      (enrollment) =>
        enrollment.status === IEnrollmentDtoStatusEnum.ACTIVE ||
        enrollment.status === IEnrollmentDtoStatusEnum.CANCELED
    );
  }, [enrollments]);

  return {
    enrollments,
    activeEnrollments,
    hasActiveEnrollments: activeEnrollments?.length > 0,
    isEnrollmentsLoading: isLoading,
  };
};

export default useGetEnrollmentsQuery;
